import { useTranslation } from 'react-i18next';

const inventoryRows = [
  {
    sail: 'Laser 4.7',
    classEq: 'ILCA 4',
    useKey: 'ilca_use_youth',
    material: '3.8 oz',
    image: 'https://public.readdy.ai/ai/img_res/20c6eff7f25baad9b936e289ede67230.jpg',
    badgeKey: 'ilca_badge_youth',
    badgeColor: 'bg-emerald-50 text-emerald-700',
  },
  {
    sail: 'Laser 5.7',
    classEq: 'ILCA 6 (Radial)',
    useKey: 'ilca_use_mid',
    material: '3.8 oz',
    image: 'https://readdy.ai/api/search-image?query=adult%20sailor%20hiking%20out%20on%20a%20Laser%20Radial%20ILCA%206%20dinghy%20sailboat%20heeling%20in%20strong%20wind%20white%20sail%20taut%20and%20full%20of%20wind%20dramatic%20action%20sailing%20photography%20open%20ocean%20blue%20water&width=600&height=480&seq=ilca6-boat-photo&orientation=landscape',
    badgeKey: 'ilca_badge_radial',
    badgeColor: 'bg-amber-50 text-amber-700',
  },
  {
    sail: 'Laser MKII',
    classEq: 'ILCA 7 (Standard)',
    useKey: 'ilca_use_full',
    material: '4.9 oz',
    image: 'https://readdy.ai/api/search-image?query=competitive%20sailor%20racing%20a%20full%20rig%20Laser%20Standard%20ILCA%207%20dinghy%20sailboat%20at%20high%20speed%20on%20choppy%20ocean%20water%20large%20white%20sail%20fully%20powered%20dramatic%20spray%20and%20wake%20professional%20sailing%20race%20photography&width=600&height=480&seq=ilca7-boat-photo&orientation=landscape',
    badgeKey: 'ilca_badge_full',
    badgeColor: 'bg-red-50 text-red-700',
  },
];

const trainingSails = [
  { labelKey: 'ilca_t1_label', descKey: 'ilca_t1_desc', icon: 'ri-seedling-line' },
  { labelKey: 'ilca_t2_label', descKey: 'ilca_t2_desc', icon: 'ri-group-line' },
  { labelKey: 'ilca_t3_label', descKey: 'ilca_t3_desc', icon: 'ri-trophy-line' },
];

export default function ILCASection() {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Section header */}
        <div className="mb-14">
          <p className="text-red-600 text-xs uppercase tracking-[0.3em] font-semibold mb-3">{t('ilca_label')}</p>
          <h2
            className="text-3xl md:text-4xl font-black text-gray-900 leading-tight"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {t('ilca_title')}
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl text-base leading-relaxed">{t('ilca_subtitle')}</p>
        </div>

        {/* Boat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-14">
          {inventoryRows.map((row) => (
            <div
              key={row.sail}
              className="rounded-2xl overflow-hidden border border-stone-200 bg-white group"
            >
              <div className="w-full h-52 overflow-hidden relative">
                <img
                  src={row.image}
                  alt={`${row.sail} sailing`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${row.badgeColor}`}>
                  {t(row.badgeKey)}
                </span>
              </div>

              <div className="p-6">
                <h3
                  className="text-gray-900 font-black text-lg mb-1"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {row.sail}
                </h3>
                <p className="text-red-600 text-xs font-semibold uppercase tracking-widest mb-4">{row.classEq}</p>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-400 font-medium">{t('ilca_rec_use')}</span>
                    <span className="text-gray-700 font-semibold">{t(row.useKey)}</span>
                  </div>
                  <div className="border-t border-stone-100" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-400 font-medium">{t('ilca_mat_weight')}</span>
                    <span className="inline-flex items-center gap-1.5 text-gray-700 font-semibold">
                      <i className="ri-scales-3-line text-stone-400" />
                      {row.material}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Training sails */}
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-8 py-6 border-b border-stone-100 flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-600/10 flex-shrink-0 mt-0.5">
              <i className="ri-flag-line text-red-600 text-lg" />
            </div>
            <div>
              <h3
                className="text-gray-900 font-black text-xl mb-1"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {t('ilca_training_title')}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-2xl">{t('ilca_training_desc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-stone-100">
            {trainingSails.map((ts) => (
              <div key={ts.labelKey} className="px-8 py-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-stone-100 mb-4">
                  <i className={`${ts.icon} text-stone-600 text-base`} />
                </div>
                <p
                  className="text-gray-900 font-bold text-sm mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {t(ts.labelKey)}
                </p>
                <p className="text-gray-500 text-sm leading-relaxed">{t(ts.descKey)}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
